import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  validateName,
  validateBusinessEmail,
  validatePhone,
  validateCompanyName,
  validateWebsite,
  formatNumericPhoneInput,
  ValidationResult,
} from "../../utils/validation";
import { apiService } from "../../services/api";
import pricingApi from "../pricing/api/pricingApi";
import { PricingPlanApi } from "../pricing/model/PricingModel";
import InputField from "../../components/InputFieldForm";
import { Industry } from "./signUpEnum";
import SelectdDropDown from "../../components/DropDownComponent";
import LocationSelector from "../../components/LocationSelector";

interface PricingPlan {
  id: string;
  name: string;
  price: string;
  period: string;
  description: string;
  yearlyPrice?: string;
  features: string[];
  cta: string;
  popular: boolean;
  badge?: string;
  disabled: boolean;
  max_users?: number;
  max_candidates?: number;
  tier_type: string;
}

const CompanySignupPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [step, setStep] = useState<1 | 2>(1);
  const [plansLoading, setPlansLoading] = useState(true);
  const [pricingPlans, setPricingPlans] = useState<PricingPlan[]>([]);
  const [selectedPhoneCode, setSelectedPhoneCode] = useState("");

  const [formData, setFormData] = useState<{
    email: string;
    first_name: string;
    last_name: string;
    country_code: string;
    phone: string;
    job_title: string;
    company_name: string;
    company_website: string;
    company_industry: string;
    company_size: string;
    selected_plan_id: string;
    location: string;
    country: string;
    state: string;
    city: string;
  }>({
    email: "",
    first_name: "",
    last_name: "",
    country_code: "",
    phone: "",
    job_title: "",
    company_name: "",
    company_website: "",
    company_industry: "",
    company_size: "",
    selected_plan_id: "",
    location: "",
    country: "",
    state: "",
    city: "",
  });

  // Fetch pricing plans on component mount
  useEffect(() => {
    const fetchPricingPlans = async () => {
      try {
        setPlansLoading(true);
        const response = await pricingApi.getPricingPlans();
        // const response = await apiService.get('/api/v1/pricing/plans');
        if (response.plans) {
          if (response.success && response.plans) {
            // setPricingPlans(response.plans);
            // API already returns plans in correct sort_order, so don't re-sort
            // Default to set the STARTED plan in sign up, this starter paln trail period is 14 days and then it will be free plan with limited features
            const freePlan = response.plans.find(
              (plan: PricingPlanApi) => plan.tier_type === "STARTER"
            );
            console.log("Fetched pricing plans:", response.plans);
            const defaultPlan = freePlan || response.plans[0];
            if (defaultPlan) {
              setFormData((prev) => ({
                ...prev,
                selected_plan_id: defaultPlan.id,
              }));
            }
          }
        } else {
          console.error("Failed to fetch pricing plans");
          setError("Failed to load pricing plans. Please refresh the page.");
        }
      } catch (error) {
        console.error("Error fetching pricing plans:", error);
        setError("Failed to load pricing plans. Please refresh the page.");
      } finally {
        setPlansLoading(false);
      }
    };

    fetchPricingPlans();
  }, []);

  const validateField = (name: string, value: string): ValidationResult => {
    switch (name) {
      case "first_name":
        return validateName(value, "First name");
      case "last_name":
        return validateName(value, "Last name");
      case "email":
        return validateBusinessEmail(value);
      case "phone":
        return validatePhone(value, true);
      case "company_name":
        return validateCompanyName(value);
      case "company_website":
        return validateWebsite(value);
      default:
        return { isValid: true, message: "" };
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    const formattedValue =
      name === "phone"
        ? formatNumericPhoneInput(value)
        : value;

    setFormData((prev) => ({ ...prev, [name]: formattedValue }));

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const applyCountryPhoneCode = (phoneCode?: string) => {
    const normalizedPhoneCode = phoneCode || "";
    setSelectedPhoneCode(normalizedPhoneCode);
    setFormData((prev) => ({
      ...prev,
      country_code: normalizedPhoneCode,
    }));

    setFieldErrors((prev) => ({ ...prev, phone: "", country: "" }));
  };

  const validateStep1 = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.first_name.trim()) {
      errors.first_name = "First name is required";
    }

    if (!formData.last_name.trim()) {
      errors.last_name = "Last name is required";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else {
      const emailValidation = validateBusinessEmail(formData.email);
      if (!emailValidation.isValid) {
        errors.email = emailValidation.message;
      }
    }

    const phoneValidation = validatePhone(formData.phone, true);
    if (!phoneValidation.isValid) {
      errors.phone = phoneValidation.message;
    }

    if (!formData.job_title.trim()) {
      errors.job_title = "Job title is required";
    }

    if (!formData.country.trim()) {
      errors.country = "Country is required";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };
  // const validateStep1 = (): boolean => {
  //   const errors: Record<string, string> = {};

  //   const firstNameValidation = validateField(
  //     "first_name",
  //     formData.first_name,
  //   );
  //   if (!firstNameValidation.isValid)
  //     errors.first_name = firstNameValidation.message;

  //   const lastNameValidation = validateField("last_name", formData.last_name);
  //   if (!lastNameValidation.isValid)
  //     errors.last_name = lastNameValidation.message;

  //   const emailValidation = validateField("email", formData.email);
  //   if (!emailValidation.isValid) errors.email = emailValidation.message;

  //   const phoneValidation = validateField("phone", formData.phone);
  //   if (!phoneValidation.isValid) errors.phone = phoneValidation.message;

  //   setFieldErrors(errors);
  //   return Object.keys(errors).length === 0;
  // };

  const validateStep2 = (): boolean => {
    const errors: Record<string, string> = {};

    const companyNameValidation = validateField(
      "company_name",
      formData.company_name
    );
    if (!companyNameValidation.isValid)
      errors.company_name = companyNameValidation.message;

    const websiteValidation = validateField(
      "company_website",
      formData.company_website
    );
    if (!websiteValidation.isValid)
      errors.company_website = websiteValidation.message;

    // // Add plan selection validation
    // if (!formData.selected_plan_id) {
    //   errors.selected_plan_id = 'Please select a pricing plan';
    // }

    setFieldErrors((prev) => ({ ...prev, ...errors }));
    return Object.keys(errors).length === 0;
  };

  const handleStep1Submit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateStep1()) {
      setError("Please fix the errors");
      return;
    }
    setError("");
    setStep(2);
  };
  const handleStep2Submit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateStep2()) {
      setError("Please fix the errors above");
      return;
    }
    handleStep3Submit(e);
    setError("");
    // setStep(3);
  };
  const handleStep3Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError("");
      setLoading(true);

      // Prepare data for company user registration (no password needed)
      const registrationData = {
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        company_name: formData.company_name,
        country_code: selectedPhoneCode,
        phone: formData.phone.trim(),
        job_title: formData.job_title,
        company_website: formData.company_website,
        company_industry: formData.company_industry,
        company_size: formData.company_size,
        location: formData.location,
        country: formData.country,
        state: formData.state,
        city: formData.city,
        selected_plan_id: formData.selected_plan_id, // Send the selected pricing plan ID
      };

      await apiService.registerCompanyUser(registrationData);

      // Redirect to email verification page with company flow
      navigate(
        `/auth/verify-email?email=${encodeURIComponent(
          formData.email
        )}&flow=company_user`
      );
    } catch (err: any) {
      console.error("Registration error:", err);

      // Better error message handling for domain conflicts
      let errorMessage = "Registration failed";
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  const IndustryTypeLabelMap: Record<Industry, string> = {
    [Industry.Technology]: "Technology",
    [Industry.Healthcare]: "Healthcare",
    [Industry.Finance]: "Finance",
    [Industry.Education]: "Education",
    [Industry.Retail]: "Retail",
    [Industry.Manufacturing]: "Manufacturing",
    [Industry.Consulting]: "Consulting",
    [Industry.Other]: "Other",
  };
  const industryOptions = Object.values(Industry).map((industry: Industry) => ({
    label: IndustryTypeLabelMap[industry],
    value: industry,
  }));
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full space-y-8">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Create Your Company Account
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Join thousands of companies using AI-powered recruitment
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center space-x-4 mb-8">
          <div
            className={`flex items-center ${
              step >= 1 ? "text-indigo-600" : "text-gray-400"
            }`}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step >= 1 ? "bg-indigo-600 text-white" : "bg-gray-200"
              }`}
            >
              1
            </div>
            <span className="ml-2 text-sm font-medium">Personal Info</span>
          </div>
          <div className="w-16 h-0.5 bg-gray-200"></div>
          <div
            className={`flex items-center ${
              step >= 2 ? "text-indigo-600" : "text-gray-400"
            }`}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step >= 2 ? "bg-indigo-600 text-white" : "bg-gray-200"
              }`}
            >
              2
            </div>
            <span className="ml-2 text-sm font-medium">Company Details</span>
          </div>
        </div>
        <div className="bg-white shadow-xl rounded-lg p-8">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
              {error}
            </div>
          )}

          {step === 1 && (
            <form onSubmit={handleStep1Submit} noValidate className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputField
                  type="text"
                  id="first_name"
                  label="First Name"
                  name="first_name"
                  placeholder="First name"
                  required
                  value={formData.first_name}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${
                    fieldErrors.first_name
                      ? "border-red-300 bg-red-50"
                      : "border-gray-300"
                  }`}
                  error={fieldErrors.first_name}
                ></InputField>
                <div>
                  <InputField
                    id="last_name"
                    label="Last Name"
                    name="last_name"
                    placeholder="Last name"
                    required
                    value={formData.last_name}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${
                      fieldErrors.last_name
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                    error={fieldErrors.last_name}
                  ></InputField>
                </div>
              </div>

              <div>
                <InputField
                  required
                  label="Work Email"
                  id="email"
                  name="email"
                  type="text"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${
                    fieldErrors.email
                      ? "border-red-300 bg-red-50"
                      : "border-gray-300"
                  }`}
                  placeholder="you@yourcompany.com"
                  error={fieldErrors.email}
                ></InputField>

                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mt-4">
                  <p className="text-sm text-blue-800">
                    <strong>Business Email Required:</strong>
                    Please use your business email address. Personal email
                    domains (Gmail, Yahoo, etc.) are not permitted for company
                    registration.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <InputField
                    label=" Job Title"
                    id="job_title"
                    name="job_title"
                    type="text"
                    required
                    value={formData.job_title}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${
                      fieldErrors.job_title
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                    placeholder="e.g., HR Manager"
                    error={fieldErrors.job_title}
                  ></InputField>
                </div>
              </div>

              <LocationSelector
                id="signup_location"
                name="location"
                required
                value={formData.location}
                countryError={fieldErrors.country}
                onCountryChange={(country) =>
                  applyCountryPhoneCode(country?.phone_code)
                }
                onChange={(value) => {
                  setFormData((prev) => ({ ...prev, location: value }));
                  if (error) setError("");
                }}
                onLocationPartsChange={(parts) => {
                  setFormData((prev) => ({
                    ...prev,
                    country: parts.country,
                    state: parts.state,
                    city: parts.city,
                  }));
                  if (error) setError("");
                }}
              />

              <div className="grid grid-cols-3 gap-3">
                <InputField
                  label="Country Code"
                  id="country_code"
                  name="country_code"
                  type="text"
                  value={formData.country_code}
                  readOnly
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50"
                  placeholder="+1"
                ></InputField>
                <div className="col-span-2">
                  <InputField
                    label="Phone Number"
                    required
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    inputMode="numeric"
                    className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${
                      fieldErrors.phone
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                    placeholder="5551234567"
                    error={fieldErrors.phone}
                  ></InputField>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Processing..." : "Continue to Company Details"}
              </button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleStep2Submit} noValidate className="space-y-6">
              <div>
                <InputField
                  label="Company Name"
                  required
                  id="company_name"
                  name="company_name"
                  type="text"
                  value={formData.company_name}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${
                    fieldErrors.company_name
                      ? "border-red-300 bg-red-50"
                      : "border-gray-300"
                  }`}
                  error={fieldErrors.company_name}
                ></InputField>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <InputField
                    label=" Company Website"
                    id="company_website"
                    name="company_website"
                    type="text"
                    value={formData.company_website}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${
                      fieldErrors.company_website
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                    placeholder="https://company.com"
                    error={fieldErrors.company_website}
                  ></InputField>
                </div>
                <div>
                  <SelectdDropDown
                    id="company_industry"
                    label="Industry"
                    placeholder="Select Industry"
                    value={formData.company_industry}
                    onChange={(value: string) => {
                      setFormData((prev) => ({
                        ...prev,
                        company_industry: value,
                      }));
                      if (fieldErrors.company_industry) {
                        setFieldErrors((prev) => ({
                          ...prev,
                          company_industry: "",
                        }));
                      }
                    }}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    options={industryOptions}
                    error={fieldErrors.company_industry}
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="company_size"
                  className="block text-sm font-medium text-gray-700"
                >
                  Company Size
                </label>
                <select
                  id="company_size"
                  name="company_size"
                  value={formData.company_size}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Select Company Size</option>
                  <option value="1-10">1-10 employees</option>
                  <option value="11-50">11-50 employees</option>
                  <option value="51-200">51-200 employees</option>
                  <option value="201-1000">201-1000 employees</option>
                  <option value="1000+">1000+ employees</option>
                </select>
              </div>

              {/* Subscription Plans */}
              <div>
                <p className="text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 mb-4">
                  Sign up today and enjoy a 14-day free trial. No charges during
                  the trial period.
                </p>
              </div>

              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-1 py-3 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-150 ease-in-out"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition duration-150 ease-in-out"
                >
                  {loading ? "Creating Account..." : "Create Company Account"}
                </button>
              </div>
            </form>
          )}

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{" "}
              <Link
                to="/auth/login"
                className="font-medium text-indigo-600 hover:text-indigo-500"
              >
                Sign in
              </Link>
            </p>
            <p className="text-xs text-gray-500 mt-4">
              By creating an account, you agree to our{" "}
              <Link
                to="/terms"
                className="text-indigo-600 hover:text-indigo-500 underline"
              >
                Terms & Conditions
              </Link>{" "}
              and{" "}
              <Link
                to="/privacy"
                className="text-indigo-600 hover:text-indigo-500 underline"
              >
                Privacy Policy
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanySignupPage;
